import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  InputAdornment,
  Button,
  Snackbar,
  Alert,
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CalculateIcon from '@mui/icons-material/Calculate';
import PageLayout from '../PageLayout';
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as ReTooltip } from 'recharts';

export default function ProsperitySimulator({ customColors }) {
  const [mode, setMode] = useState('compound'); // 'simple' | 'compound'
  const [initial, setInitial] = useState('0');
  const [monthly, setMonthly] = useState('0');
  const [increaseType, setIncreaseType] = useState('percent'); // 'percent' | 'amount'
  const [increaseValue, setIncreaseValue] = useState('0');
  const [rate, setRate] = useState('0'); // % ao mês
  const [years, setYears] = useState('0');
  const [months, setMonths] = useState('0');
  const [hasCalculated, setHasCalculated] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const normalizeNumberInput = (value) => {
    if (!value) return 0;
    const cleaned = String(value)
      .replace(/[^\d,.-]/g, '')
      .replace(/\./g, '')
      .replace(',', '.');
    const parsed = parseFloat(cleaned);
    return Number.isNaN(parsed) ? 0 : parsed;
  };

  // Carregar configurações salvas no primeiro acesso
  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      const raw = window.localStorage.getItem('habitop_prosperity_simulator');
      if (!raw) return;
      const saved = JSON.parse(raw);
      if (typeof saved.rate === 'string') setRate(saved.rate);
      if (typeof saved.years === 'string') setYears(saved.years);
      if (typeof saved.months === 'string') setMonths(saved.months);
    } catch {
      // ignora erros de leitura/parse
    }
  }, []);

  // Persistir configurações localmente sempre que mudarem
  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      const data = JSON.stringify({ rate, years, months });
      window.localStorage.setItem('habitop_prosperity_simulator', data);
    } catch {
      // ignora erros de escrita
    }
  }, [rate, years, months]);

  const totalMonths = useMemo(() => {
    const y = parseInt(years || '0', 10);
    const m = parseInt(months || '0', 10);
    const t = y * 12 + m;
    return t > 0 ? t : 0;
  }, [years, months]);

  const simulationRows = useMemo(() => {
    if (totalMonths <= 0) return [];

    const r = normalizeNumberInput(rate) / 100 || 0;
    const initialAmount = normalizeNumberInput(initial) || 0;
    const monthlyBase = normalizeNumberInput(monthly) || 0;
    const incValue = normalizeNumberInput(increaseValue) || 0;

    // Se todos os valores estiverem zerados, não exibe ainda a tabela/grafico
    const hasAnyValue =
      initialAmount > 0 || monthlyBase > 0 || incValue > 0 || r > 0;
    if (!hasAnyValue) return [];

    // principal: soma de todos os aportes (inicial + mensais + adicionais)
    // cumulativeReturn: soma de todos os rendimentos (juros)
    let principal = initialAmount;
    let cumulativeReturn = 0;
    let currentMonthly = monthlyBase;
    let lastInterest = 0;

    const rows = [];
    for (let i = 1; i <= totalMonths; i += 1) {
      const monthIndex = i - 1;
      const yearNumber = Math.floor(monthIndex / 12) + 1;

      const isFirstMonth = monthIndex === 0;

      // aporte base do mês (só a partir do mês 2)
      const aporteBase = isFirstMonth ? 0 : currentMonthly;

      // aporte adicional (aumento do aporte mensal)
      const additionalContribution =
        isFirstMonth
          ? 0
          : increaseType === 'percent'
          ? currentMonthly * (incValue / 100 || 0)
          : incValue;

      const totalContribution = aporteBase + additionalContribution;

      // novos aportes aumentam apenas o principal
      principal += totalContribution;

      // juros:
      // - simples: apenas sobre os aportes (principal, sem juros sobre juros)
      // - compostos: sobre todo o saldo (aportes + rendimentos acumulados)
      const baseForInterest =
        mode === 'simple' ? principal : principal + cumulativeReturn;
      const interest = r > 0 ? baseForInterest * r : 0;

      cumulativeReturn += interest;

      const finalBalance = principal + cumulativeReturn;

      // quanto a renda (juros) cresceu em relação ao mês anterior, em R$
      const incomeGrowthAmount = monthIndex === 0 ? interest : interest - lastInterest;

      rows.push({
        month: i,
        year: yearNumber,
        // investimento nominal considerando que o aporte mensal começa no mês 2
        investment: initialAmount + monthlyBase * Math.max(0, i - 1),
        contribution: aporteBase,
        additionalContribution,
        startingBalance: baseForInterest,
        interest,
        cumulativeReturn,
        finalBalance,
        incomeGrowthAmount,
      });

      // aumento do aporte mensal só após o mês 1 (quando ele passa a existir)
      if (!isFirstMonth && incValue !== 0) {
        currentMonthly +=
          increaseType === 'percent'
            ? currentMonthly * (incValue / 100 || 0)
            : incValue;
      }

      lastInterest = interest;
    }

    return rows;
  }, [totalMonths, rate, initial, monthly, increaseType, increaseValue, mode]);

  const formatCurrency = (v) =>
    new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
      maximumFractionDigits: 2,
    }).format(Number.isFinite(v) ? v : 0);

  const formatPercent = (v) =>
    `${(Number.isFinite(v) ? v : 0).toFixed(2).replace('.', ',')}%`;

  const summary = useMemo(() => {
    if (simulationRows.length === 0) {
      return {
        totalInvested: 0,
        totalContributions: 0,
        totalAdditional: 0,
        totalReturn: 0,
        roi: 0,
        monthsCount: 0,
        finalBalance: 0,
      };
    }

    const monthsCount = simulationRows.length;
    const finalRow = simulationRows[simulationRows.length - 1];
    const totalContributions = simulationRows.reduce(
      (sum, r) => sum + r.contribution,
      0
    );
    const totalAdditional = simulationRows.reduce(
      (sum, r) => sum + r.additionalContribution,
      0
    );
    const totalInvested = normalizeNumberInput(initial) + totalContributions + totalAdditional;
    const totalReturn = finalRow.cumulativeReturn;
    const finalBalance = finalRow.finalBalance;
    const roi = totalInvested > 0 ? (totalReturn / totalInvested) * 100 : 0;

    return {
      totalInvested,
      totalContributions,
      totalAdditional,
      totalReturn,
      roi,
      monthsCount,
      finalBalance,
    };
  }, [simulationRows, initial]);

  const totals = useMemo(() => {
    if (simulationRows.length === 0) {
      return {
        investment: 0,
        contribution: 0,
        additionalContribution: 0,
        startingBalance: 0,
        interest: 0,
        cumulativeReturn: 0,
        finalBalance: 0,
        incomeGrowthAmount: 0,
      };
    }

    return simulationRows.reduce(
      (acc, row) => ({
        investment: acc.investment + row.investment,
        contribution: acc.contribution + row.contribution,
        additionalContribution: acc.additionalContribution + row.additionalContribution,
        startingBalance: acc.startingBalance + row.startingBalance,
        interest: acc.interest + row.interest,
        cumulativeReturn: acc.cumulativeReturn + row.cumulativeReturn,
        finalBalance: acc.finalBalance + row.finalBalance,
        incomeGrowthAmount: acc.incomeGrowthAmount + row.incomeGrowthAmount,
      }),
      {
        investment: 0,
        contribution: 0,
        additionalContribution: 0,
        startingBalance: 0,
        interest: 0,
        cumulativeReturn: 0,
        finalBalance: 0,
        incomeGrowthAmount: 0,
      }
    );
  }, [simulationRows]);

  return (
    <PageLayout title="Simulador" hideAddButton customColors={customColors}>
      <Box sx={{ maxWidth: 1000, mx: 'auto', py: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <CalculateIcon sx={{ fontSize: 40, color: 'text.secondary' }} />
          <Box>
            <Typography variant="h6" color="primary">
              Simulador de Investimentos
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Compare a evolução dos seus investimentos com juros simples ou compostos, mês a mês.
            </Typography>
          </Box>
        </Box>

        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Tipo de juros
          </Typography>
          <ToggleButtonGroup
            color="primary"
            exclusive
            value={mode}
            onChange={(_, v) => v && setMode(v)}
            size="small"
          >
            <ToggleButton value="simple">Juros simples</ToggleButton>
            <ToggleButton value="compound">Juros compostos</ToggleButton>
          </ToggleButtonGroup>
        </Box>

        {/* Cards de resumo */}
        {hasCalculated && simulationRows.length > 0 && (
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, minmax(0, 1fr))' },
              gap: 2,
              mb: 2,
            }}
          >
            <Paper sx={{ p: 2 }}>
              <Typography variant="caption" color="text.secondary">
                Total aportado
              </Typography>
              <Typography variant="h6" sx={{ color: 'primary.main' }}>
                {formatCurrency(summary.totalInvested)}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Inclui aporte inicial, mensal e adicionais.
              </Typography>
            </Paper>
            <Paper sx={{ p: 2 }}>
              <Typography variant="caption" color="text.secondary">
                Total de rendimentos
              </Typography>
              <Typography variant="h6" sx={{ color: 'success.main' }}>
                {formatCurrency(summary.totalReturn)}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Rendimento acumulado ao longo de todo o período.
              </Typography>
            </Paper>
            <Paper sx={{ p: 2 }}>
              <Typography variant="caption" color="text.secondary">
                Retorno (% ROI)
              </Typography>
              <Typography variant="h6" sx={{ color: 'primary.main' }}>
                {formatPercent(summary.roi)}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Sobre o total aportado.
              </Typography>
            </Paper>
            <Paper sx={{ p: 2 }}>
              <Typography variant="caption" color="text.secondary">
                Meses simulados
              </Typography>
              <Typography variant="h6" sx={{ color: 'primary.main' }}>
                {summary.monthsCount}
              </Typography>
            </Paper>
            <Paper sx={{ p: 2 }}>
              <Typography variant="caption" color="text.secondary">
                Saldo final
              </Typography>
              <Typography variant="h6" sx={{ color: '#1976d2' }}>
                {formatCurrency(summary.finalBalance)}
              </Typography>
            </Paper>
          </Box>
        )}

        <Paper sx={{ p: 2, mb: 3 }}>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, minmax(0, 1fr))' },
              gap: 2,
            }}
          >
            <TextField
              label="Aporte inicial"
              type="text"
              size="small"
              value={initial}
              onChange={(e) => setInitial(e.target.value)}
              onBlur={() =>
                setInitial(
                  normalizeNumberInput(initial).toLocaleString('pt-BR', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })
                )
              }
              InputProps={{
                startAdornment: <InputAdornment position="start">R$</InputAdornment>,
              }}
              sx={{ '& .MuiInputBase-input': { color: 'primary.main' } }}
            />
            <TextField
              label="Aporte mensal"
              type="text"
              size="small"
              value={monthly}
              onChange={(e) => setMonthly(e.target.value)}
              onBlur={() =>
                setMonthly(
                  normalizeNumberInput(monthly).toLocaleString('pt-BR', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })
                )
              }
              InputProps={{
                startAdornment: <InputAdornment position="start">R$</InputAdornment>,
                endAdornment: (
                  <InputAdornment position="end">
                    <Tooltip title="O aporte mensal só começa a ser considerado a partir do mês 2. O mês 1 usa apenas o aporte inicial.">
                      <InfoOutlinedIcon fontSize="small" sx={{ color: 'text.secondary', cursor: 'help' }} />
                    </Tooltip>
                  </InputAdornment>
                ),
              }}
              sx={{ '& .MuiInputBase-input': { color: 'primary.main' } }}
            />
            <TextField
              label="Taxa de juros"
              type="text"
              size="small"
              value={rate}
              onChange={(e) => setRate(e.target.value)}
              onBlur={() =>
                setRate(
                  normalizeNumberInput(rate).toLocaleString('pt-BR', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })
                )
              }
              InputProps={{
                endAdornment: <InputAdornment position="end">%</InputAdornment>,
              }}
              sx={{ '& .MuiInputBase-input': { color: 'primary.main' } }}
            />
          </Box>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, minmax(0, 1fr))' },
              gap: 2,
              mt: 2,
            }}
          >
            <TextField
              label={
                increaseType === 'percent'
                  ? 'Aumentar aporte mensal em (% ao mês)'
                  : 'Aumentar aporte mensal em (R$ ao mês)'
              }
              type="text"
              size="small"
              value={increaseValue}
              onChange={(e) => setIncreaseValue(e.target.value)}
              onBlur={() =>
                setIncreaseValue(
                  normalizeNumberInput(increaseValue).toLocaleString('pt-BR', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })
                )
              }
              InputProps={{
                startAdornment:
                  increaseType === 'amount' ? (
                    <InputAdornment position="start">R$</InputAdornment>
                  ) : undefined,
                endAdornment:
                  increaseType === 'percent' ? (
                    <InputAdornment position="end">%</InputAdornment>
                  ) : undefined,
              }}
              helperText="Pode ser 0 se não quiser aumentar o aporte."
              sx={{ '& .MuiInputBase-input': { color: 'primary.main' } }}
            />
            <ToggleButtonGroup
              color="primary"
              exclusive
              value={increaseType}
              onChange={(_, v) => v && setIncreaseType(v)}
              size="small"
            >
              <ToggleButton value="percent">% ao mês</ToggleButton>
              <ToggleButton value="amount">R$ fixos ao mês</ToggleButton>
            </ToggleButtonGroup>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <TextField
                label="Anos"
                type="number"
                size="small"
                value={years}
                onChange={(e) => setYears(e.target.value)}
                InputProps={{ inputProps: { min: 0, max: 100, step: 1 } }}
                sx={{ '& .MuiInputBase-input': { color: 'primary.main' } }}
              />
              <TextField
                label="Meses"
                type="number"
                size="small"
                value={months}
                onChange={(e) => setMonths(e.target.value)}
                InputProps={{ inputProps: { min: 0, max: 1200, step: 1 } }}
                sx={{ '& .MuiInputBase-input': { color: 'primary.main' } }}
              />
            </Box>
          </Box>

          <Typography variant="caption" color="text.secondary" sx={{ mt: 1.5, display: 'block' }}>
            Dica: 30 anos = 360 meses. A tabela abaixo se adapta ao número total de meses escolhidos.
          </Typography>

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
            <Button
              variant="contained"
              color="primary"
              onClick={() => {
                const totalM = totalMonths;
                const r = normalizeNumberInput(rate) / 100 || 0;
                const initialAmount = normalizeNumberInput(initial) || 0;
                const monthlyBase = normalizeNumberInput(monthly) || 0;
                const incVal = normalizeNumberInput(increaseValue) || 0;
                const hasAny =
                  initialAmount > 0 || monthlyBase > 0 || incVal > 0 || r > 0;

                if (totalM <= 0 || !hasAny) {
                  setSnackbar({
                    open: true,
                    message: 'Informe o período (anos/meses) e ao menos um valor (aporte inicial, aporte mensal, aumento de aporte ou taxa de juros) para calcular.',
                    severity: 'error',
                  });
                  return;
                }
                setHasCalculated(true);
                setSnackbar({
                  open: true,
                  message: 'Cálculo efetuado com sucesso',
                  severity: 'success',
                });
              }}
            >
              Calcular
            </Button>
          </Box>
        </Paper>

        {!hasCalculated || simulationRows.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            Defina um período em anos/meses para ver a simulação mês a mês.
          </Typography>
        ) : (
          <>
            <Paper sx={{ p: 2, mb: 3 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Evolução do patrimônio
              </Typography>
              <Box sx={{ width: '100%', height: 260 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={simulationRows}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="month" tickFormatter={(m) => `M${m}`} />
                    <YAxis />
                    <ReTooltip
                      formatter={(value, name) =>
                        name === 'finalBalance'
                          ? [formatCurrency(value), 'Saldo final']
                          : [formatCurrency(value), '']
                      }
                      labelFormatter={(label) => `Mês ${label}`}
                    />
                    <Line
                      type="monotone"
                      dataKey="finalBalance"
                      stroke="#1976d2"
                      strokeWidth={2}
                      dot={false}
                      name="Saldo final"
                    />
                  </LineChart>
                </ResponsiveContainer>
              </Box>
            </Paper>

            <TableContainer component={Paper} sx={{ maxHeight: 500, overflowX: 'visible' }}>
              <Table size="small" stickyHeader sx={{ minWidth: 1050 }}>
                <TableHead>
                  <TableRow>
                    <TableCell>Mês</TableCell>
                    <TableCell>Ano</TableCell>
                    <TableCell>Investimento</TableCell>
                    <TableCell>Aportes</TableCell>
                    <TableCell>Aporte adicional</TableCell>
                    <TableCell>Saldo Inicial</TableCell>
                    <TableCell>Rendimento</TableCell>
                    <TableCell>Rendimento acumulado</TableCell>
                    <TableCell>Saldo Final</TableCell>
                    <TableCell>Renda cresceu em:</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {simulationRows.map((row) => (
                    <TableRow key={row.month} hover>
                      <TableCell>{row.month}</TableCell>
                      <TableCell>{row.year}</TableCell>
                      <TableCell>{formatCurrency(row.investment)}</TableCell>
                      <TableCell>{formatCurrency(row.contribution)}</TableCell>
                      <TableCell>{formatCurrency(row.additionalContribution)}</TableCell>
                      <TableCell>{formatCurrency(row.startingBalance)}</TableCell>
                      <TableCell sx={{ color: 'success.main', fontWeight: 500 }}>
                        {formatCurrency(row.interest)}
                      </TableCell>
                      <TableCell>{formatCurrency(row.cumulativeReturn)}</TableCell>
                      <TableCell sx={{ color: '#1976d2', fontWeight: 500 }}>
                        {formatCurrency(row.finalBalance)}
                      </TableCell>
                      <TableCell>{formatCurrency(row.incomeGrowthAmount)}</TableCell>
                    </TableRow>
                  ))}
                <TableRow
                  sx={{
                    bgcolor: (theme) =>
                      theme.palette.mode === 'dark'
                        ? 'rgba(255,255,255,0.04)'
                        : 'rgba(25, 118, 210, 0.04)',
                  }}
                >
                  <TableCell sx={{ fontWeight: 600 }}>Total</TableCell>
                  <TableCell />
                  <TableCell sx={{ fontWeight: 600 }}>
                    {formatCurrency(totals.investment)}
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>
                    {formatCurrency(totals.contribution)}
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>
                    {formatCurrency(totals.additionalContribution)}
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>
                    {formatCurrency(totals.startingBalance)}
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, color: 'success.main' }}>
                    {formatCurrency(totals.interest)}
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>
                    {formatCurrency(totals.cumulativeReturn)}
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, color: '#1976d2' }}>
                    {formatCurrency(totals.finalBalance)}
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>
                    {formatCurrency(totals.incomeGrowthAmount)}
                  </TableCell>
                </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}

        <Snackbar
          open={snackbar.open}
          autoHideDuration={snackbar.severity === 'error' ? 6000 : 4000}
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert
            onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
            severity={snackbar.severity}
            sx={{ width: '100%' }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    </PageLayout>
  );
}
